import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Stack,
  Tab,
  TabList,
  Tabs,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { FiMoreVertical, FiSearch } from "react-icons/fi";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Loading } from "../../../components/Loading";
import {
  getFleetsList,
} from "../../../actions/inventory/fleet.action";
import {
  getVerificationReviews,
  updateVerificationDecision,
} from "../../../actions/verificationReview.action";

const ITEMS_PER_PAGE = 10;

const reviewStatusColorMap = {
  under_review: "yellow",
  clarification_requested: "orange",
  approved: "green",
  rejected: "red",
};

const administrativeStatusMap = {
  0: "Available",
  1: "On Duty",
  2: "Under Maintenance",
  3: "Out Of Service",
};

const administrativeStatusColorMap = {
  0: "blue",
  1: "green",
  2: "orange",
  3: "red",
};

const legacyOperationalStatusMap = {
  0: "in_review",
  1: "active",
  2: "inactive",
  3: "blacklisted",
};

const normalizeOperationalStatus = (value) =>
  legacyOperationalStatusMap[value] || value || "in_review";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLabel = (value = "") =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const deriveReviewStatus = (row) => {
  if (row.reviewStatus) return row.reviewStatus;
  const operational = normalizeOperationalStatus(row.operationalStatus);
  const administrative = Number(row.administrativeStatus);
  if (operational === "active" && administrative === 1) return "approved";
  if (operational === "blacklisted" || administrative === 3) return "rejected";
  return "under_review";
};

const getTabStatus = (status) =>
  ["under_review", "clarification_requested"].includes(status) ? "pending" : status;

const getDocumentsStatus = (documents) => {
  if (!documents) return "Missing";
  const required = [
    documents?.registrationCertificate?.front,
    documents?.registrationCertificate?.back,
    documents?.insurance?.document,
    documents?.puc?.document,
    documents?.permit?.document,
    documents?.tax?.document,
  ];
  return required.every((value) => typeof value === "string" && value.trim()) ? "Complete" : "Incomplete";
};

const VehicleApprovals = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("pending");
  const debounceTimer = useRef(null);


  const loadData = useCallback(async () => {
    setFetching(true);
    try {
      const response = await getFleetsList(currentPage, ITEMS_PER_PAGE, query);
      let reviews = [];

      try {
        const reviewResponse = await getVerificationReviews({
          entityType: "vehicle",
          limit: 200,
          search: query,
        });
        reviews = reviewResponse?.data || [];
      } catch (error) {
        console.error(error.message);
      }

      const reviewByEntityId = new Map(
        reviews
          .filter((review) => review.entityId)
          .map((review) => [String(review.entityId), review]),
      );

      const normalized = (response?.data || []).map((item) => {
        const documents = item?.documents || {};
        const operationalStatus = normalizeOperationalStatus(item?.status?.operationalStatus);
        const administrativeStatus = Number(item?.status?.administrativeStatus ?? 0);
        const review = reviewByEntityId.get(String(item._id));
        return {
          id: item._id,
          entityId: item._id,
          reviewId: review?._id || null,
          vehicleNumber: item?.vehicleNumber || "—",
          vehicleType: item?.vehicle?.segmentName || item?.vehicleType || "—",
          model: item?.model || "—",
          manufacturer: item?.manufacturer || "—",
          ownerType: item?.ownershipDetails?.ownershipType || "—",
          ownerId:
            item?.ownershipDetails?.driverId ||
            item?.ownershipDetails?.vendorId ||
            null,
          submittedAt: item?.createdAt || null,
          documents,
          documentsStatus: getDocumentsStatus(documents),
          operationalStatus,
          administrativeStatus,
          reviewStatus: review?.status || deriveReviewStatus({
            operationalStatus,
            administrativeStatus,
          }),
          clarificationMessages: review?.clarificationMessages || [],
          raw: item,
        };
      });

      const existingEntityIds = new Set(normalized.map((item) => String(item.entityId)));
      const reviewOnlyRows = reviews
        .filter((review) => !review.entityId || !existingEntityIds.has(String(review.entityId)))
        .map((review) => ({
          id: review._id,
          entityId: review.entityId || null,
          reviewId: review._id,
          reviewOnly: true,
          vehicleNumber: review.displayCode || review.displayName || "—",
          vehicleType: review.title || "—",
          model: "—",
          manufacturer: "—",
          ownerType: "—",
          ownerId: null,
          submittedAt: review.submittedAt || review.createdAt || null,
          documents: review.documents || {},
          documentsStatus: Object.keys(review.documents || {}).length ? "Complete" : "Incomplete",
          operationalStatus:
            review.status === "approved"
              ? "active"
              : review.status === "rejected"
                ? "blacklisted"
                : "in_review",
          administrativeStatus: review.status === "rejected" ? 3 : 0,
          reviewStatus: review.status,
          clarificationMessages: review.clarificationMessages || [],
          raw: review,
        }));

      setData([...reviewOnlyRows, ...normalized]);
      setTotalPages(Math.max(1, Number(response?.totalPages) || 1));
    } catch (error) {
      toast({
        title: "Failed to fetch vehicle approvals",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setData([]);
      setTotalPages(1);
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, [currentPage, query, toast]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setQuery(searchInput.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredData = useMemo(() => {
    if (statusFilter === "all") return data;
    return data.filter((row) => getTabStatus(row.reviewStatus) === statusFilter);
  }, [data, statusFilter]);

  const statusTabs = ["pending", "approved", "rejected", "all"];

  const openDetails = (vehicle) => {
    navigate(`/verification/vehicle-approvals/${vehicle.id}`, {
      state: { row: vehicle },
    });
  };

  const updateLocalVehicle = (vehicleId, updater) => {
    setData((prev) =>
      prev.map((row) =>
        row.id === vehicleId
          ? {
            ...row,
            ...updater(row),
          }
          : row,
      ),
    );
  };

  const handleApprove = async (vehicle) => {
    try {
      // Entity-level decision: the account becomes Active only via the review rule
      const response = await updateVerificationDecision({
        entityType: "vehicle",
        entityId: vehicle.entityId || vehicle.id,
        reviewId: vehicle.reviewId,
        decision: "approve",
        row: vehicle,
      });
      updateLocalVehicle(vehicle.id, () => ({
        operationalStatus: "active",
        administrativeStatus: 1,
        reviewStatus: "approved",
        reviewId: response?.data?._id || vehicle.reviewId,
      }));
      toast({
        title: "Vehicle approved",
        description: "The approval is saved for the app verification status.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to approve vehicle",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) return <Loading />;

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/verification", title: "Verification Center" },
          { link: "#", title: "Vehicle Approvals", on: true },
        ]}
      />

      <Box bg="white" borderRadius="xl" p={4} shadow="sm">
        <Flex justify="space-between" align="center" mb={4} gap={4} wrap="wrap">
          <Box>
            <Text fontSize="xl" fontWeight="bold" color="gray.700">
              Vehicle Approvals
            </Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Live fleet records from the inventory backend
            </Text>
          </Box>

          <Box position="relative">
            <Input
              placeholder="Search by vehicle number or owner..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              pl={10}
              borderRadius="lg"
              w={{ base: "100%", md: "320px" }}
              bg="gray.50"
            />
            <Box position="absolute" left={3} top="50%" transform="translateY(-50%)">
              <FiSearch color="gray" />
            </Box>
          </Box>
        </Flex>

        <Tabs
          variant="soft-rounded"
          colorScheme="blue"
          mb={4}
          index={statusTabs.indexOf(statusFilter)}
          onChange={(index) => setStatusFilter(statusTabs[index] || "pending")}
        >
          <TabList>
            <Tab>Pending</Tab>
            <Tab>Approved</Tab>
            <Tab>Rejected</Tab>
            <Tab>All</Tab>
          </TabList>
        </Tabs>

        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Vehicle Number</Th>
                <Th>Vehicle Type</Th>
                <Th>Model</Th>
                <Th>Submitted At</Th>
                <Th>Documents</Th>
                <Th>Review Status</Th>
                <Th>Admin Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fetching ? (
                <Tr>
                  <Td colSpan={8} py={8} textAlign="center">
                    <Loading />
                  </Td>
                </Tr>
              ) : filteredData.length === 0 ? (
                <Tr>
                  <Td colSpan={8} py={8} textAlign="center">
                    <Text color="gray.500">No vehicle approvals found</Text>
                  </Td>
                </Tr>
              ) : (
                filteredData.map((row) => {
                  const reviewStatus = row.reviewStatus;
                  return (
                    <Tr key={row.id} _hover={{ bg: "gray.50" }}>
                      <Td onClick={() => openDetails(row)} fontWeight="medium">{row.vehicleNumber}</Td>
                      <Td onClick={() => openDetails(row)}>{row.vehicleType}</Td>
                      <Td onClick={() => openDetails(row)}>{row.model}</Td>
                      <Td onClick={() => openDetails(row)}>{formatDate(row.submittedAt)}</Td>
                      <Td onClick={() => openDetails(row)}>
                        <Flex align="center" gap={2}>
                          <Badge
                            colorScheme={row.documentsStatus === "Complete" ? "green" : "orange"}
                          >
                            {row.documentsStatus}
                          </Badge>
                        </Flex>
                      </Td>
                      <Td onClick={() => openDetails(row)}>
                        <Badge colorScheme={reviewStatusColorMap[row.reviewStatus] || "gray"}>
                          {formatLabel(row.reviewStatus)}
                        </Badge>
                      </Td>
                      <Td onClick={() => openDetails(row)}>
                        <Badge colorScheme={administrativeStatusColorMap[row.administrativeStatus] || "gray"}>
                          {administrativeStatusMap[row.administrativeStatus] || "—"}
                        </Badge>
                      </Td>
                      <Td>
                        <Menu placement="bottom-end">
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem onClick={() => openDetails(row)}>View Details</MenuItem>
                            {reviewStatus !== "approved" && (
                              <MenuItem color="green.500" onClick={() => handleApprove(row)}>
                                Approve
                              </MenuItem>
                            )}
                            {reviewStatus !== "rejected" && (
                              <MenuItem color="red.500" onClick={() => openDetails(row)}>
                                Reject
                              </MenuItem>
                            )}
                            <MenuItem color="orange.500" onClick={() => openDetails(row)}>
                              Request Clarification
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>

                  )
                }))}
            </Tbody>
          </Table>
        </Box>

        <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={3}>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">
              Rows per page:
            </Text>
            <Select size="sm" value={ITEMS_PER_PAGE} w="74px" isDisabled>
              <option value={10}>10</option>
            </Select>
          </Flex>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">
              Page {currentPage} of {totalPages}
            </Text>
            <IconButton
              icon={<GoChevronLeft />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === 1 || fetching}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              aria-label="Previous page"
            />
            <IconButton
              icon={<GoChevronRight />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === totalPages || fetching}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              aria-label="Next page"
            />
          </Flex>
        </Flex>
      </Box>

    </Stack>
  );
};

export default VehicleApprovals;
